import { Box, Button, Card, CardContent, CardHeader, Grid, List, ListItem, Theme, Typography } from "@mui/material";
import { ICustomTab, IFullFlight, PolicyResource } from "@vmosolutions/oc-interfaces";
import { IExtendedJSONSchema6 } from "src/components/Custom/CustomFieldsV2";
import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { CustomValues } from "src/components/Custom/CustomValues";
import { addEventToTimeline } from "src/redux/actions/flights/FlightData.Actions";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { store } from "src/redux/store/Store";
import { FlightGroupChip } from "../Flights/FlightGroupChip";

interface ISummaryDisplayProps {
  mosaicId: string;
  /** The items to display in the summary view */
  items: ISummaryItem[];
  flights?: IFullFlight[];
  /** Color to make the item cards */
  color?: string;
  /** Title of the summary card */
  title?: string;
}

/** This is how we expect items to be expressed to us */
export interface ISummaryItem {
  /** The key to reference this particular item */
  key: string;
  /** Data to display */
  value: string | number;
  /** A visually appealing way of displaying the Key */
  label?: string;
}

interface ICustomCategory {
  title: string;
  schema: IExtendedJSONSchema6 | null;
}

/**
 * Takes a list of Summary Items and displays them as a list of cards.
 * @param props - Properties of the component, see [[ISummaryDisplayProps]]
 */
export const SummaryDisplay = (props: ISummaryDisplayProps): JSX.Element => {
  const { mosaicId, items } = props;

  const [flights, setFlights] = useState<IFullFlight[]>(props?.flights);
  const [loadingFlight, setLoadingFlight] = useState<boolean>(false);

  /** If summary is displaying only one flight, allow user to refresh the info disaplyed. */
  const getFullFlightAction = useCallback(async (id: string): Promise<void> => {
    setLoadingFlight(true);
    const response = await AmplifyAPIWrapper.Get<IFullFlight>(`/flights/${id}`);
    if (response?._id) {
      addEventToTimeline(response, "flight", store.dispatch, () => store.getState());
      setFlights([response]);
    }
    setLoadingFlight(false);
  }, []);

  const uniqueKeys: string[] = [];
  const customFlightTabs: ICustomTab[] = useSelector((state: IRootState) => state.customPages.flight);
  const customCategories: ICustomCategory[] = useMemo(() => {
    return customFlightTabs?.length
      ? customFlightTabs
          ?.sort((a, b) => a.tabIndex - b.tabIndex)
          .map((custom) => ({
            title: custom.title,
            schema: custom.customSchema ? JSON.parse(custom.customSchema) : null,
          }))
      : [];
  }, [customFlightTabs]);

  const mosaicPane = useMemo(() => document.getElementById(`${mosaicId}-panel`), [mosaicId]);

  // Grab all the unique keys from the items.
  items.forEach((item) => {
    if (!uniqueKeys.includes(item.key)) uniqueKeys.push(item.key);
  });

  const displayItems: ISummaryItem[] = [];
  uniqueKeys.forEach((key) => {
    const keyItems = items.filter((item) => item.key === key);
    const result =
      typeof keyItems[0].value === "string"
        ? Array.from(new Set(keyItems.map((item) => item.value))).join("; ")
        : keyItems.map((item) => item.value as number).reduce((acc, curr) => acc + curr);
    displayItems.push({
      key,
      value: result,
      label: keyItems[0].label,
    });
  });

  const SummaryDisplayStyles = (theme: Theme, cardColor?: string): any => ({
    summaryCard: {
      position: "relative",
      backgroundColor: cardColor ? cardColor : theme.custom.timeline.contextListBackground,
      height: "fit-content",
      width: "100%",
      maxWidth: "400px",
      margin: "1rem",
    },
    flightGroup: {
      position: "absolute",
      top: 0,
      right: 0,
      zIndex: 1,
    },
    summaryItemLabel: {
      fontSize: 11,
      fontWeight: "bold",
    },
    summaryItemValue: {
      fontSize: 11,
      fontWeight: "normal",
    },
    summaryItem: {
      display: "flex",
      flexDirection: "row",
      flexWrap: "nowrap",
      justifyContent: "space-between",
      paddingTop: "2px",
      paddingBottom: "2px",
    },
  });

  // Return the list, styled, mapping each item to a card with a key unique enough for the scope of the list.
  return (
    <Card square={true} sx={(theme) => SummaryDisplayStyles(theme, props.color).summaryCard}>
      <CardHeader
        title={"Flight Summary"}
        subheader={
          flights?.length === 1 && (
            <Grid container>
              <Button
                style={{ marginLeft: "auto", marginRight: 0, height: 24, fontSize: 11 }}
                className="btn refresh"
                variant="contained"
                color="primary"
                disabled={loadingFlight}
                onClick={() => getFullFlightAction(flights[0]?._id)}
              >
                Refresh
              </Button>
            </Grid>
          )
        }
      />
      <Box sx={(theme) => SummaryDisplayStyles(theme, props.color).flightGroup}>
        <FlightGroupChip data={flights} />
      </Box>
      <CardContent>
        <List disablePadding>
          {displayItems.map((item: ISummaryItem, index: number) => (
            <ListItem
              key={`${item.key}-${item.value}-${index}`}
              sx={(theme) => SummaryDisplayStyles(theme, props.color).summaryItem}
            >
              <Typography sx={(theme) => SummaryDisplayStyles(theme, props.color).summaryItemLabel}>
                {item.label}
              </Typography>
              <Typography sx={(theme) => SummaryDisplayStyles(theme, props.color).summaryItemValue}>
                {item.value}
              </Typography>
            </ListItem>
          ))}
        </List>
        {/*  Only show custom fields if display summary for a single flight */}
        <Grid container direction="column">
          {flights?.length === 1 &&
            flights[0]?.custom &&
            customCategories?.map((customCat) => (
              <Grid item key={`custom-content-${customCat?.title}`}>
                <CustomValues
                  schema={customCat?.schema}
                  customValues={flights[0]?.custom}
                  mosaicPane={mosaicPane}
                  resource={PolicyResource.Flight}
                  displayHeader={customCat?.title}
                />
              </Grid>
            ))}
        </Grid>
      </CardContent>
    </Card>
  );
};
